import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { applyBrand } from "../lib/brand";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const ACTIVE_KEY = "momento_active_sub";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, obj=user
  const [tenant, setTenant] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [activeSubId, setActiveSubId] = useState(localStorage.getItem(ACTIVE_KEY) || null);

  const loadContext = useCallback(async () => {
    const [{ data: t }, { data: subs }] = await Promise.all([
      api.get("/tenant"),
      api.get("/sub-accounts"),
    ]);
    setTenant(t);
    setSubAccounts(subs);
    let active = localStorage.getItem(ACTIVE_KEY);
    if (!active || !subs.find((s) => s.id === active)) {
      active = subs[0]?.id || null;
    }
    setActiveSubId(active);
    if (active) localStorage.setItem(ACTIVE_KEY, active);
    const activeSub = subs.find((s) => s.id === active);
    applyBrand(activeSub?.branding?.primary_color || t?.branding?.primary_color);
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("momento_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadContext();
    } catch {
      localStorage.removeItem("momento_token");
      setUser(false);
    }
  }, [loadContext]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("momento_token", data.access_token);
    setUser(data.user);
    await loadContext();
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("momento_token", data.access_token);
    setUser(data.user);
    await loadContext();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("momento_token");
    setUser(false);
    setTenant(null);
    setSubAccounts([]);
  };

  const switchSubAccount = (id) => {
    setActiveSubId(id);
    localStorage.setItem(ACTIVE_KEY, id);
    const sub = subAccounts.find((s) => s.id === id);
    applyBrand(sub?.branding?.primary_color || tenant?.branding?.primary_color);
  };

  const refreshSubAccounts = useCallback(async () => {
    const { data } = await api.get("/sub-accounts");
    setSubAccounts(data);
  }, []);

  const refreshTenant = useCallback(async () => {
    const { data } = await api.get("/tenant");
    setTenant(data);
  }, []);

  const activeSub = subAccounts.find((s) => s.id === activeSubId) || null;

  return (
    <AuthContext.Provider value={{
      user, tenant, subAccounts, activeSub, activeSubId,
      login, register, logout, switchSubAccount, refreshSubAccounts, refreshTenant, formatApiError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
